import { getCookie } from './utils';
import { COMMON_SETTING } from './constants';

/**
 * 获取项目枚举值
 * @param {String} projectName - 项目名称
 * @param {Object} cookieConfig - Cookie配置
 * @returns {Number|String} - 项目枚举值
 */
function getProjectEnumValue({ projectName, cookieConfig }) {
  return cookieConfig?.project?.[projectName] || '';
}

/**
 * 获取项目名称
 * @param {Number|String} projectEnumValue - 项目枚举值
 * @param {Object} cookieConfig - Cookie配置
 * @returns {String} - 项目名称
 */
function getProjectName({ projectEnumValue, cookieConfig }) {
  for (const name in cookieConfig.project) {
    if (`${cookieConfig.project[name]}` === `${projectEnumValue}`) {
      return name;
    }
  }
  return '';
}

/**
 * 根据值获取对象中对应的键
 * @param {Object} specificConfig - 特定配置
 * @param {Object} generalConfig - 通用配置
 * @param {Number|String} value - 值
 * @returns {String} - 对应的键
 * @throws {Error} - 未找到匹配的键时抛出错误
 */
function getKeyByValue(specificConfig, generalConfig, value) {
  // 优先在特定配置中查找
  if (specificConfig) {
    for (const key in specificConfig) {
      if (specificConfig[key] === value) {
        return key;
      }
    }
  }

  // 其次在通用配置中查找
  if (generalConfig) {
    for (const key in generalConfig) {
      if (generalConfig[key] === value) {
        return key;
      }
    }
  }

  // 如果都没有匹配，抛出错误
  throw new Error(`未找到匹配的键: ${value}`);
}

/**
 * 校验cookieConfig配置
 * @param {Object} cookieConfig - 配置对象
 * @returns {Object} - 校验结果，包含isValid和errors字段
 */
function validateConfig(cookieConfig) {
  const errors = [];

  // 校验project字段
  if (
    !cookieConfig?.project ||
    Object.keys(cookieConfig.project).length === 0
  ) {
    errors.push('cookieConfig.project为必填项且不能为空');
  }

  // 校验paths字段
  if (
    !cookieConfig?.paths ||
    !Array.isArray(cookieConfig.paths) ||
    cookieConfig.paths.length === 0
  ) {
    errors.push('cookieConfig.paths为必填项且必须是非空数组');
    return { isValid: errors.length === 0, errors };
  }

  // 校验paths中的每个配置项
  cookieConfig.paths.forEach((pathConfig, index) => {
    if (!pathConfig.path) {
      errors.push(`cookieConfig.paths[${index}].path为必填项`);
    }

    if (!pathConfig.layerId) {
      errors.push(`cookieConfig.paths[${index}].layerId为必填项`);
    }

    if (!pathConfig.abType) {
      errors.push(`cookieConfig.paths[${index}].abType为必填项`);
    }
  });

  return {
    isValid: errors.length === 0,
    errors,
  };
}

/**
 * 检查解码值是否有效
 * @param {*} value - 要检查的值
 * @returns {Boolean} - 值是否无效
 */
function isInvalidValue(value) {
  return value === undefined || value === null;
}

/**
 * 根据编码类型生成编码结果
 * @param {String} cookieVal - Cookie值
 * @param {Object} cookieConfig - Cookie配置
 * @returns {Array} - 编码部分数组
 */
function generateEncodedParts({ cookieVal, cookieConfig = {} }) {
  // 对象类型兼容ehc容器抵达率取数逻辑，后期ehc容器改造后可以去掉
  if (cookieConfig.type === 'object') {
    const cookieValObj = JSON.parse(cookieVal);
    return cookieValObj?.report?.pgy_ab?.split('|') || [];
  }
  // 默认处理字符串类型
  return cookieVal.split('|');
}

/**
 * 解码AB实验数据
 * @param {String} encodedData - 编码后的字符串
 * @param {Object} cookieConfig - Cookie配置
 * @returns {Object|null} - 解码后的原始AB实验数据
 * @throws {Error} - 配置校验失败或解码过程中出错时抛出错误
 */
function decodeABData({ encodedData, cookieConfig }) {
  // 校验配置
  const validation = validateConfig(cookieConfig);
  if (!validation.isValid) {
    throw new Error('配置校验失败: ' + validation.errors.join(', '));
  }

  if (!encodedData) {
    console.log('解码数据为空');
    return null;
  }

  // 提取编码数据部分
  const match = encodedData.match(/_pgy_wink_(\d+)\s*=\s*(.*)/);
  if (!match) {
    throw new Error(`解码数据格式错误: ${encodedData}`);
  }

  const projectEnumValue = match[1];
  const encodedParts = generateEncodedParts({
    cookieVal: match[2],
    cookieConfig,
  });

  // 获取项目名称
  const projectName = getProjectName({ projectEnumValue, cookieConfig });
  if (!projectName) {
    throw new Error(`未找到项目名称: ${projectEnumValue}`);
  }

  // 创建结果对象
  const result = {
    config: {},
    report: {},
  };

  // 填充config部分
  cookieConfig.paths.forEach((pathConfig) => {
    result.config[pathConfig.abType] = pathConfig.layerId;
  });

  // 解析每个编码部分
  encodedParts.forEach((part) => {
    if (part.length !== 4) {
      throw new Error(`编码部分长度异常: ${part}`);
    }
    const [pathIndex, abStrategyValue, abSwitchValue, abFromValue] = part;

    // 获取对应的path配置
    const pathConfig = cookieConfig.paths[pathIndex];

    if (pathConfig) {
      const layerId = pathConfig.layerId;

      // 获取实验分组的原始值
      const abStrategy = getKeyByValue(
        pathConfig.abStrategy,
        COMMON_SETTING.AB_STRATEGY,
        abStrategyValue
      );

      // 获取实验策略的原始值
      const abSwitch = getKeyByValue(
        pathConfig.abSwitch,
        COMMON_SETTING.AB_SWITCH,
        abSwitchValue
      );

      // 获取实验来源的原始值
      const abFrom = getKeyByValue(
        pathConfig.abFrom,
        COMMON_SETTING.AB_FROM,
        abFromValue
      );

      // 如果有任何一个值未找到匹配，取消解码
      if (
        isInvalidValue(abStrategy) ||
        isInvalidValue(abSwitch) ||
        isInvalidValue(abFrom)
      ) {
        throw new Error(`解码失败，未找到匹配的键: layerId=${layerId}`);
      }

      // 填充report部分
      let newReport = {
        ...result.report,
        [layerId]: abStrategy,
        [`${layerId}_s`]: abSwitch,
        [`${layerId}_f`]: abFrom,
      };

      // 同步率数据大盘取值数据源保证统一
      if (pathConfig.abType === 'abSyncLayerId') {
        newReport = {
          ...newReport,
          pgy_ab: abStrategy,
          pgy_ab_s: abSwitch,
          pgy_ab_f: abFrom,
        };
      }

      // 更新report
      result.report = newReport;
    } else {
      throw new Error(`未找到对应的path配置: ${pathIndex}`);
    }
  });

  return result;
}

/**
 * 从Cookie中获取AB实验数据
 * @param {String} abType - AB测试类型
 * @param {Object} cookieConfig - Cookie配置
 * @returns {Object} - 解码后的AB实验数据
 */
export function getABFromCookie({ abType, cookieConfig }) {
  // 兼容历史未编码逻辑
  if (!cookieConfig) {
    return JSON.parse(getCookie(`_pgy_wink_${abType}`) || '{}');
  }

  // 获取项目枚举值
  const projectEnumValue = getProjectEnumValue({
    projectName: abType,
    cookieConfig,
  });

  // 获取编码后的cookieKey
  const cookieKey = `_pgy_wink_${projectEnumValue}`;

  // 获取编码后的cookieVal
  const cookieVal = getCookie(cookieKey);

  // 未命中实验直接返回
  if (!cookieVal) return {};

  // 解码数据格式
  const encodedData = `${cookieKey} = ${cookieVal}`;

  // 解码AB实验数据
  const decodedData = decodeABData({ encodedData, cookieConfig });

  return decodedData || {};
}
